import { ipqsCheck } from "../../../services/ipqs";
import { muteLeaveModal } from '../../../utils/checkout';
import calculateHash from "../../../services/fingerprintjs2";

// stripe service mixin for handling Stripe API operations
export default {
  methods: {
    // helper method to save user data to localStorage
    saveUserDataToStorage(name, email, phone) {
      if (name) localStorage.setItem("stripe_username", name);
      if (email) localStorage.setItem("stripe_user_email", email);
      if (phone) localStorage.setItem("stripe_user_phone", phone);
    },

    // helper method to save shipping address data to localStorage
    saveShippingAddressToStorage(address) {
      if (address) {
        localStorage.setItem("stripe_shipping_address", JSON.stringify({
          street: address.line1 || '',
          city: address.city || '',
          zip: address.postal_code || '',
          state: address.state || '',
          country: address.country ? address.country.toLowerCase() : ''
        }));
      }
    },

    async initStripe() {
      try {
        const stripePublishableKey = await this.getKey(this.method);

        if (!stripePublishableKey) {
          console.warn('Failed to get Stripe publishable key');
          this.initializationFailed = true;
          return false;
        }

        this.stripe = Stripe(stripePublishableKey);

        // initialize elements without client secret - payment intent will be created when Google Pay is clicked
        const amountForStripeElementsInit = this.currencyMultiplier === 1
          ? parseInt(this.amount / 100) // for zero-decimal currencies like JPY (this.amount is base * 100)
          : parseInt(this.amount);      // for other currencies (this.amount is base * 100, which is correct minor unit)

        this.elements = this.stripe.elements({
          mode: 'payment',
          amount: amountForStripeElementsInit,
          currency: this.currency,
          appearance: {
            theme: 'stripe'
          }
        });

        return true;
      } catch (error) {
        console.error('Stripe initialization error:', error);
        this.initializationFailed = true;
        return false;
      }
    },

    async updatePaymentAmount(newAmount) {
      try {
        // only update the amount in elements, don't create payment intent yet
        // payment intent will be created when Google Pay button is clicked
        const amountForStripeElementsUpdate = this.currencyMultiplier === 1
          ? parseInt(newAmount / 100) // for zero-decimal currencies like JPY (newAmount is base * 100)
          : parseInt(newAmount);      // for other currencies (newAmount is base * 100, which is correct minor unit)

        this.elements.update({
          amount: amountForStripeElementsUpdate,
          currency: this.currency
        });

        // clear existing payment intent since amount changed
        this.paymentIntent = null;
        this.order_id = null;
        this.order_currency = null;
        this.order_number = null;
        this.odin_order_id = null;
      } catch (error) {
        console.warn("Failed to update payment amount:", error);
      }
    },

    async getKey(method = "browser_card") {
      try {
        const response = await fetch("/wallet-key?method=" + method + "&product_id=" + js_data.product.id);
        const data = await response.json();
        return data.key;
      } catch (err) {
        console.warn("Cannot get Stripe key", err.message);
        return null;
      }
    },

    async createPaymentIntent() {
      muteLeaveModal(); // disable discount popup

      try {
        let bodyData = {};
        if (this.upsells) {
          bodyData = {
            order: localStorage.getItem("odin_order_id"),
            upsells: this.upsells
          };
        } else {
          bodyData = {
            product: {
              qty: this.quantity || 1,
              sku: this.sku,
              upsells: this.upsells,
              is_warranty_checked: this.warranty
            },
            method: this.method
          };

          // for Google Pay, use data from localStorage collected during confirm event
          // for other methods, use data from payment form
          if (this.method === 'google_pay') {
            // use data collected from Google Pay (stored in localStorage)
            const gpayEmail = localStorage.getItem("stripe_user_email");
            const gpayPhone = localStorage.getItem("stripe_user_phone");
            const gpayName = localStorage.getItem("stripe_username");
            const gpayShippingAddress = localStorage.getItem("stripe_shipping_address");
            
            if (gpayEmail || gpayPhone || gpayName) {
              console.log('Using Google Pay data for order creation:', {
                email: gpayEmail,
                phone: gpayPhone,
                name: gpayName,
                hasAddress: !!gpayShippingAddress
              });
              
              // parse name into first and last name
              let firstName = '', lastName = '';
              if (gpayName) {
                const nameParts = gpayName.trim().split(' ');
                firstName = nameParts[0] || '';
                lastName = nameParts.slice(1).join(' ') || '';
              }
              
              // validate and clean phone number
              const cleanPhone = (gpayPhone || '').replace(/[^0-9]/g, '');
              
              bodyData.contact = {
                first_name: firstName,
                last_name: lastName,
                email: gpayEmail || '',
                phone: {
                  country_code: '1', // default country code
                  number: cleanPhone.length >= 10 ? cleanPhone : '0000000000' // use fallback if phone is invalid
                }
              };
              
              // Add address data from Google Pay if available
              if (gpayShippingAddress) {
                try {
                  const addressData = JSON.parse(gpayShippingAddress);
                  bodyData.address = {
                    street: addressData.street || '',
                    city: addressData.city || '',
                    country: addressData.country || this.getLocationCountryFromPage() || 'us',
                    zip: addressData.zip || '',
                    state: addressData.state || ''
                  };
                } catch (error) {
                  console.warn('Error parsing Google Pay address data:', error);
                  // Fallback to country detection only
                  bodyData.address = {
                    street: '',
                    city: '',
                    country: this.getLocationCountryFromPage() || 'us',
                    zip: '',
                    state: ''
                  };
                }
              }
            }
          } else if (this.paymentFormData && this.validatePersonalData()) {
            // use data from payment form for other methods
            const dialCode = this.getDialCode();
            const phoneNumber = (this.paymentFormData.phone || '').replace(/[^0-9]/g, '');
            
            bodyData.contact = {
              first_name: this.paymentFormData.fname,
              last_name: this.paymentFormData.lname,
              email: this.paymentFormData.email,
              phone: {
                country_code: dialCode,
                number: phoneNumber
              }
            };

            bodyData.address = {
              street: this.paymentFormData.street,
              city: this.paymentFormData.city,
              country: this.paymentFormData.country,
              zip: this.paymentFormData.zipcode || '00000',
              state: this.paymentFormData.state || ''
            };
          }

          // add IPQS check only for main payment, not for upsells
          bodyData.ipqs = await ipqsCheck({
            order_amount: this.amount / 100
          });

          // add fingerprint for main payment, not for upsells
          try {
            bodyData.f = await calculateHash();
          } catch (err) {
            console.warn("Cannot get fingerprint", err.message);
          }
        }
        
        const response = await fetch(this.intentRequestUri, {
          method: "post",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify(bodyData)
        });
        
        // Clear Google Pay data from localStorage after sending to backend
        if (this.method === 'google_pay') {
          localStorage.removeItem("stripe_shipping_address");
        }

        if (!response.ok) {
          console.error('HTTP error:', response.status, response.statusText);
          return { error: `HTTP ${response.status}: ${response.statusText}` };
        }

        const data = await response.json();
        
        if (data.error) {
          console.error('Server returned error:', data.error);
          return { error: data.error };
        } else {
          this.paymentIntent = {
            id: data.id,
            client_secret: data.client_key,
            amount: data.order_amount,
            currency: data.order_currency,
            status: "requires_payment_method"
          };

          this.order_id = data.id;
          this.order_currency = data.order_currency;
          this.order_number = data.order_number;
          this.odin_order_id = data.order_id;

          return data;
        }
      } catch (err) {
        console.error('Error creating payment intent:', err);
        return { error: err.message || 'Unknown error occurred' };
      }
    },

    handlePayment(paymentResponse) {
      const { paymentIntent, error } = paymentResponse;

      if (paymentIntent) {
        this.paymentIntent = paymentIntent;
      }

      if (error) {
        console.error("Error during payment:", error);
        this.status = "error";
        this.errorMessage = error.message || "Payment failed";
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        this.status = "success";

        localStorage.setItem("odin_order_id", this.odin_order_id);
        localStorage.setItem("order_currency", this.order_currency);
        localStorage.setItem("order_number", this.order_number);
        localStorage.setItem("order_id", this.order_id);

        this.onSuccess();
      } else if (paymentIntent && paymentIntent.status === "processing") {
        this.status = "success";
      } else if (paymentIntent && paymentIntent.status === "requires_payment_method") {
        // failure. Requires new PaymentMethod, show last payment error message.
        this.status = "error";
        this.errorMessage = paymentIntent.last_payment_error?.message || "Payment method required";
      } else {
        this.status = "error";
        this.errorMessage = "Payment failed";
      }
    },

    // Helper method to get country from page context
    getLocationCountryFromPage() {
      try {
        // Try to get country from js_query_params if available
        if (window.js_query_params && window.js_query_params.country) {
          return window.js_query_params.country.toLowerCase();
        }
        
        // Try to get country from js_data if available
        if (window.js_data && window.js_data.country) {
          return window.js_data.country.toLowerCase();
        }
        
        // Try to get from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const countryParam = urlParams.get('country');
        if (countryParam) {
          return countryParam.toLowerCase();
        }
        
        // Default fallback
        return 'us';
      } catch (error) {
        console.warn('Error getting country from page:', error);
        return 'us';
      }
    }
  }
};